import { useState } from "react";
import { FilmStrip, Spinner, ArrowSquareOut } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * <ExerciseDemoButton name="Bench Press" /> renders a small "Show form" icon
 * button. On click it lazy-loads a demo image + description from /api/exercise-demo
 * (which caches the wger.de result server-side) and shows it in a modal.
 *
 * Renders nothing visually heavy until clicked, so it's safe to drop next to
 * every exercise row.
 */
export default function ExerciseDemoButton({ api, name, label = "Show form", className = "", size = 14 }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    setError("");
    try {
      const url = `${api}/exercise-demo?name=${encodeURIComponent(name)}`;
      const res = await fetch(url);
      if (!res.ok) {
        setError(res.status === 404 ? "no_demo" : "error");
        return;
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
          load();
        }}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#E05A1D] font-bold transition-colors ${className}`}
        title="Show exercise demo"
        data-testid={`demo-btn-${name}`}
      >
        <FilmStrip size={size} weight="bold" />
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#161616] border-[#27272A] max-w-md" data-testid="exercise-demo-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {data?.name || name}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-10 flex flex-col items-center gap-2 text-[#71717A]" data-testid="exercise-demo-loading">
              <Spinner size={24} className="animate-spin text-[#E05A1D]" />
              <p className="text-xs uppercase tracking-wider">Looking up demo…</p>
            </div>
          )}

          {!loading && error === "no_demo" && (
            <div className="py-6 text-center" data-testid="exercise-demo-no-demo">
              <p className="text-sm text-[#A1A1AA]">No demo found for this exercise.</p>
              <p className="text-xs text-[#71717A] mt-1">Tip: try renaming it to a more standard exercise name (e.g. "Bench Press" instead of "BP").</p>
            </div>
          )}

          {!loading && error === "error" && (
            <div className="py-6 text-center">
              <p className="text-sm text-red-400">Could not fetch the demo. Try again.</p>
            </div>
          )}

          {!loading && data && (
            <div className="space-y-3" data-testid="exercise-demo-content">
              {data.image ? (
                <img
                  src={data.image}
                  alt={data.name}
                  className="w-full max-h-72 object-contain bg-[#0F0F0F] border border-[#27272A]"
                />
              ) : (
                <div className="w-full h-40 bg-[#0F0F0F] border border-[#27272A] flex items-center justify-center text-[#71717A] text-xs uppercase tracking-wider">
                  No image available
                </div>
              )}

              {data.muscles?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {data.muscles.map((m, i) => (
                    <span key={i} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#E05A1D]/40 bg-[#E05A1D]/10 text-[#E05A1D]">
                      {m}
                    </span>
                  ))}
                  {data.muscles_secondary?.map((m, i) => (
                    <span key={`s-${i}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">
                      {m}
                    </span>
                  ))}
                </div>
              )}

              {data.description && (
                <p className="text-xs text-[#A1A1AA] leading-relaxed">{data.description}</p>
              )}

              <a
                href={`https://wger.de/en/exercise/${data.wger_id}/view`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] text-[#71717A] hover:text-[#E05A1D] uppercase tracking-wider font-bold transition-colors"
              >
                Open full guide on wger.de <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
